<template>
  <v-card>
    <v-card-text>
      <v-card-title class="text-subtitle-1 font-weight-bold"
        >Kanäle</v-card-title
      >
      <v-tabs v-model="activeChannel" bg-color="primary" slider-color="warning">
        <v-tab
          v-for="(channel, index) in config.channels"
          :key="index"
          :value="index"
        >
          <span class="text-overline">{{ SWW_CHANNELS[index].name }}</span>
        </v-tab>
      </v-tabs>
      <v-tabs-window v-model="activeChannel">
        <v-tabs-window-item
          v-for="(_channel, index) in config.channels"
          :key="index"
          :value="index"
        >
          <SwwChannel v-model="config" :channel-nr="index" />
        </v-tabs-window-item>
      </v-tabs-window>
      <v-divider />
      <v-card-title class="text-subtitle-1 font-weight-bold"
        >Regeln</v-card-title
      >
      <v-tabs v-model="activeRule" bg-color="primary" slider-color="warning">
        <v-tab
          v-for="(_rule, index) in config.rules"
          :key="index"
          :value="index"
        >
          <span class="text-overline">{{ `Regel: ${index.toString()}` }}</span>
        </v-tab>
      </v-tabs>
      <v-tabs-window v-model="activeRule">
        <v-tabs-window-item
          v-for="(rule, index) in config.rules"
          :key="index"
          :value="index"
        >
          <SwwRule v-model="config" :rule="rule" :rule-nr="index" />
        </v-tabs-window-item>
      </v-tabs-window>
      <v-divider />
      <ContainerWithLabel label="Verzögerung [s]" class="text-caption px-2">
        <template #append-content>
          <v-btn
            v-tooltip:top="
              `Definiert die Verzögerung nach Erhalt des Sensorwertes bis zum Absetzen des Befehls.`
            "
            icon="mdi-help-circle-outline"
            density="compact"
            size="small"
            variant="text"
            color="primary"
          />
        </template>
        <v-number-input
          v-model="config.delay.value"
          type="number"
          size="small"
          density="compact"
          width="250px"
          :step="0.01"
          :min="0.13"
          :max="16"
        />
      </ContainerWithLabel>
    </v-card-text>
    <v-card-actions>
      <v-btn color="primary" variant="flat" @click="closeModal"
        >Übernehmen</v-btn
      >
      <v-btn color="error" variant="text" @click="cancel">Abbrechen</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import hotkeys from 'hotkeys-js'
import isEqual from 'lodash.isequal'
import merge from 'lodash.merge'
import { onBeforeMount, onMounted, onUnmounted, Ref, ref } from 'vue'

import { createDefaultSww } from '@/apps/brelag/common/models/brelagSwwDefault'
import { SWW_CHANNELS, SwwConfig } from '@/apps/brelag/common/models/swwParams'
import ContainerWithLabel from '@/components/common/ContainerWithLabel.vue'
import { useConfirmationDialog } from '@/util/composables/useConfirmationDialog'

import SwwChannel from './SwwChannel.vue'
import SwwRule from './SwwRule.vue'

const emit = defineEmits(['close'])

const configRef = ref<SwwConfig>(null)
const config = defineModel<SwwConfig>('config', { default: createDefaultSww() })
const activeChannel = ref(0)
const activeRule = ref(0)
const originalConfig = ref<SwwConfig>(null)

onBeforeMount(() => {
  configRef.value = JSON.parse(JSON.stringify(config.value))
})
onMounted(() => {
  hotkeys('esc', { scope: 'SwwConfigEditor', keyup: true }, (event) => {
    // only fire event on keyup to avoid closing confirm dialog directly
    if (event.type === 'keyup') {
      event.preventDefault()
      cancel()
    }
  })
  hotkeys.setScope('SwwConfigEditor')

  if (config.value.delay === null) {
    config.value.delay = {
      value: 1,
    }
  }
  // keep original config to detect changes
  originalConfig.value = JSON.parse(JSON.stringify(config.value))
})

onUnmounted(() => {
  hotkeys.unbind('esc', 'SwwConfigEditor')
  hotkeys.setScope('EditModal')
})

function closeModal() {
  emit('close')
}

const confirm = useConfirmationDialog()
async function cancel() {
  if (isEqual(config.value, originalConfig.value)) {
    // no changes, close immediatly
    closeModal()
  } else {
    const isConfirmed = await confirm({
      content: 'Sind Sie sicher, dass Sie die Änderung verwerfen möchten?',
    })
    if (isConfirmed) {
      // revert changes
      // need to merge into config object, cannot reassign config.value to a new object
      merge(config.value, originalConfig.value)
      closeModal()
    }
  }
}
</script>

<style lang="scss">
.b-tabs .tab-content {
  overflow: visible !important;
}
</style>
